import isGHMC, {
  formatDateTimeWithoutSec,
  getCurrentDateTime,
} from "../../../../Common/helper/helpar";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useEffect } from "react";
import { TransferstationMonthlyFilters } from "./TransferstationMonthlyReportFilter";
import { TransferstationManagementStore } from "./store/transferstationManagementStore";
import TableSkeleton from "../../../AllotedMaster/TableSkeleton";

const TransferstationManagementMonthlyReport = () => {
  const {
    transferstationManagementReportData,
    loading,
    currentFilters,
  } = TransferstationManagementStore();

  const isProd = isGHMC();
  const currentDate = getCurrentDateTime();

  /* ================= GROUP DATA BY DATE ================= */
  const groupByDate = (data) => {
    const map = {};
    data.forEach((item) => {
      const date = item.ReportDate;
      if (!map[date]) map[date] = [];
      map[date].push(item);
    });
    return map;
  };

  const groupedByDate = groupByDate(
    transferstationManagementReportData || []
  );
  const dateKeys = Object.keys(groupedByDate);

  /* ================= UNIQUE LOCATIONS ================= */
  const locations = Array.from(
    new Map(
      (transferstationManagementReportData || []).map((item) => [
        item.TransferStationId,
        item,
      ])
    ).values()
  );

  /* ================= METRIC FIELDS ================= */
  const excludedFields = [
    "ReportDate",
    "TransferStationId",
    "TransferStationName",
    "Total",
    "TSSCTPRFC",
    "TS_SCTP_RFC",
    // "TS/SCTP/RFC",
    "StationType",
  ];

  const metricFields = Array.from(
    new Set(
      (transferstationManagementReportData || []).flatMap((item) =>
        Object.keys(item).filter((k) => !excludedFields.includes(k))
      )
    )
  );

  /* ================= HELPERS ================= */
  const formatDateOnly = (dateString) => {
    if (!dateString) return "";
    try {
      // If dateString contains time, extract only date part
      const datePart = dateString.split(" ")[0]; // Split by space and take first part
      const date = new Date(datePart);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).replace(/\//g, "-");
    } catch (error) {
      return dateString;
    }
  };

  const getValue = (stationId, date, field) => {
    const record = groupedByDate[date]?.find(
      (r) => r.TransferStationId === stationId
    );
    return record?.[field] ?? 0;
  };

  const getTotal = (stationId, date) => {
  return metricFields.reduce((sum, field) => {
    const value = Number(getValue(stationId, date, field)) || 0;
    return sum + value;
  }, 0);
};


  /* ================= GRAND TOTAL HELPERS ================= */
  const getGrandTotalForField = (date, field) => {
     if (field.toLowerCase().includes("description")) return "";
    return locations.reduce(
      (sum, location) =>
        sum + Number(getValue(location.TransferStationId, date, field)),
      0
    );
  };

  const getGrandTotalForDate = (date) => {
    return locations.reduce(
      (sum, location) => sum + Number(getTotal(location.TransferStationId, date)),
      0
    );
  };

  /* ================= STYLES ================= */
  const headerStyle = {
    textAlign: "center",
    border: "1px solid #ddd",
    padding: "10px",
    color: "#fff",
    backgroundColor: isProd ? "#0d5da5" : "#EC1117",
    fontWeight: 600,
    whiteSpace: "nowrap",
  };

  const cellStyle = {
    textAlign: "center",
    border: "1px solid #ddd",
    padding: "8px",
  };

  /* ================= DEBUG ================= */
  useEffect(() => {
    if (transferstationManagementReportData?.length) {
      console.log("Dates:", dateKeys);
      console.log("Metrics:", metricFields);
    }
  }, [transferstationManagementReportData]);


  return (
    <>
      <div style={{ width: "100%" }}>
        <div className="content">
          <div className="page-content">
            <TransferstationMonthlyFilters />
            <div className="page-header text-start">
              <div className="card">
                <div className="card-body">
                  <div className="top-header text-center" style={{ width: "100%" }}>
                    {/* <div className="mb-2 fw-bold" style={{ textAlign: "center" }}>
                      Transferstation Management Monthly Report Generated on: {formatDateTimeWithoutSec(currentDateTime)}
                    </div> */}
                    <span  style={{ textAlign: "center" }}>
                      Transferstation Management Monthly Report as on ({currentFilters?.FromDate && currentFilters?.ToDate
                        ? `${currentFilters.FromDate} to ${currentFilters.ToDate}`
                        : "—"}) at ({formatDateTimeWithoutSec(currentDate)})
                       
                      
                    </span>
                  </div>
                  {loading ? (
                    <TableSkeleton />
                  ) : (
                    <TableContainer component={Paper} sx={{ maxHeight: 700 }}>
                    <Table stickyHeader>
                      {/* ================= HEADER ================= */}
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headerStyle} rowSpan={2}>
                            S.No
                          </TableCell>
                          <TableCell sx={headerStyle} rowSpan={2}>
                            Location Name
                          </TableCell>
                          <TableCell sx={headerStyle} rowSpan={2}>
                            TS/SCTP/RFC
                          </TableCell>
                         

                          {dateKeys.map((date) => (
                            <TableCell
                              key={date}
                              colSpan={metricFields.length + 1}
                              sx={{
                                ...headerStyle,
                                position: "sticky",
                                top: 0,          // stick on vertical scroll
                                zIndex: 4,       // higher than body + sub headers
                                backgroundColor: isProd ? "#0d5da5" : "#EC1117",
                              }}
                            >
                              {formatDateOnly(date)}
                            </TableCell>

                          ))}
                        </TableRow>

                        <TableRow>
                          {dateKeys.map((date) => (
                            <>
                              {metricFields.map((field) => (
                                <TableCell key={field} sx={{
                                  ...headerStyle,
                                  position: "sticky",
                                  top: 48,   // height of date header row
                                  zIndex: 4,
                                  backgroundColor: isProd ? "#0d5da5" : "#EC1117",
                                }}>
                                  {field}
                                </TableCell>
                              ))}
                              <TableCell
                                sx={{
                                  ...headerStyle,
                                  position: "sticky",
                                  top: 48,
                                  zIndex: 4,
                                  backgroundColor: isProd ? "#0d5da5" : "#EC1117",
                                }}
                              >
                                Total
                              </TableCell>

                            </>
                          ))}
                        </TableRow>
                      </TableHead>

                      {/* ================= BODY ================= */}
                      <TableBody>
                        {locations.map((row, index) => (
                          <TableRow
                            key={row.TransferStationId}
                            sx={{
                              backgroundColor: isProd
                                ? index % 2 === 0
                                  ? "#8fc9ff"
                                  : "#aed8ff"
                                : index % 2 === 0
                                  ? "#F8CCCD"
                                  : "#ffeded",
                            }}
                          >
                            <TableCell sx={cellStyle}>{index + 1}</TableCell>
                            <TableCell sx={cellStyle}>
                              {row.TransferStationName}
                            </TableCell>
                            <TableCell sx={cellStyle}>
                              {row.StationType || "N/A"}
                            </TableCell>
                           
                            {dateKeys.map((date) => (
                              <>
                                {metricFields.map((field) => (
                                  <TableCell key={field} sx={cellStyle}>
                                    {getValue(row.TransferStationId, date, field)}
                                  </TableCell>
                                ))}
                                <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>
                                  {getTotal(row.TransferStationId, date)}
                                </TableCell>
                              </>
                            ))}
                          </TableRow>
                        ))}

                        {/* ================= GRAND TOTAL ROW ================= */}
                        <TableRow
                          sx={{
                            backgroundColor: isProd ? "#0d5da5" : "#EC1117",
                            "& .MuiTableCell-root": {
                              color: "#fff",
                              fontWeight: 700,
                            },
                          }}
                        >
                          <TableCell sx={cellStyle}></TableCell>
                          <TableCell sx={cellStyle}></TableCell>

                          <TableCell sx={cellStyle}>Grand Total</TableCell>
                          {dateKeys.map((date) => (
                            
                            <>
                              {metricFields.map((field) => (
                                <TableCell key={field} sx={cellStyle}>
                                  {getGrandTotalForField(date, field)}
                                </TableCell>
                              ))}
                              <TableCell sx={cellStyle}>
                                {getGrandTotalForDate(date)}
                              </TableCell>
                            </>
                          ))}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TransferstationManagementMonthlyReport;
